import { useState, useEffect, useMemo, useRef, useCallback } from 'preact/hooks';
import { GameStateHook } from '../utils/useGameState';
import { api, DailyAttemptState } from '../utils/api';
import { generateDailyPuzzle } from '../game/generator';
import { registerGeneratedDaily } from '../game/levels';

interface Props {
  onBack: () => void;
  onPlayLevel: (levelId: number) => void;
  gameState: GameStateHook;
}

const MAX_PURCHASED_ATTEMPTS = 10;
const ATTEMPT_PRICE_STARS = 10;
const PAYMENT_REFRESH_DELAY_MS = 1000;

function getTodayKey(): string {
  return new Date().toISOString().split('T')[0];
}

export function DailyPuzzle({ onBack, onPlayLevel, gameState }: Props) {
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [attemptState, setAttemptState] = useState<DailyAttemptState | null>(null);
  const [attemptError, setAttemptError] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [consuming, setConsuming] = useState(false);
  const consumingRef = useRef(false);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Generate daily puzzle client-side (deterministic from date)
  const dailyLevelId = useMemo(() => {
    const puzzle = generateDailyPuzzle(getTodayKey());
    return registerGeneratedDaily(puzzle);
  }, []);

  const refetchAttempts = useCallback(() => {
    return api.getDailyAttempts()
      .then(data => {
        setAttemptState(data);
        setAttemptError(false);
      })
      .catch(() => setAttemptError(true));
  }, []);

  useEffect(() => {
    api.getDailyLeaderboard()
      .then(lb => setLeaderboard(lb))
      .catch(() => {})
      .finally(() => setLoading(false));

    refetchAttempts();

    return () => {
      if (refreshTimerRef.current !== null) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [refetchAttempts]);

  const attemptLoading = attemptState === null && !attemptError;
  const attemptsRemaining = attemptState?.attemptsRemaining ?? 0;
  const maxAttempts = attemptState?.maxAttempts ?? 1;
  const purchased = attemptState?.purchased ?? 0;
  const canBuyAttempt = purchased < MAX_PURCHASED_ATTEMPTS;
  const canPlay = !attemptLoading && !attemptError && attemptsRemaining > 0 && !consuming;

  const handlePlay = async () => {
    // Guard against double-tap / concurrent consumption: if a request is already
    // in flight, ignore further clicks. We use a ref (not state) for the guard
    // because React/Preact state updates don't propagate within the same event
    // loop tick, so rapid synchronous clicks would all see `consuming === false`.
    // Without this guard, rapid taps would fire multiple POST /api/daily/attempt
    // requests and the server's atomic upsert would consume one attempt per
    // request — charging the user for attempts they never played.
    if (!canPlay || consumingRef.current) return;
    consumingRef.current = true;
    setConsuming(true);
    try {
      const data = await api.consumeDailyAttempt();
      setAttemptState(data);
      onPlayLevel(dailyLevelId);
    } catch {
      const tg = (window as any).Telegram?.WebApp;
      tg?.showAlert?.('Unable to start attempt. Please try again.');
      // Refresh state in case it's stale
      refetchAttempts();
    } finally {
      consumingRef.current = false;
      setConsuming(false);
    }
  };

  const handleBuyAttempt = async () => {
    setPurchasing(true);
    try {
      const result = await api.purchase('daily_attempt');
      if (result?.invoiceUrl) {
        const tg = (window as any).Telegram?.WebApp;
        tg?.openInvoice?.(result.invoiceUrl, (status: string) => {
          if (status === 'paid') {
            // Wait briefly for the bot's successful_payment handler to record the purchase, then refresh
            refreshTimerRef.current = setTimeout(() => {
              refreshTimerRef.current = null;
              refetchAttempts().finally(() => setPurchasing(false));
            }, PAYMENT_REFRESH_DELAY_MS);
          } else {
            setPurchasing(false);
          }
        });
      } else {
        setPurchasing(false);
      }
    } catch {
      setPurchasing(false);
      const tg = (window as any).Telegram?.WebApp;
      tg?.showAlert?.('Purchase failed. Please try again.');
    }
  };

  const today = getTodayKey();

  return (
    <div style={styles.container}>
      <div style={styles.topBar}>
        <button style={styles.backBtn} onClick={onBack}>Back</button>
        <h2 style={styles.title}>Daily Puzzle</h2>
        <div style={{ width: 50 }} />
      </div>

      <div style={styles.content}>
        <div style={styles.dateCard}>
          <span style={styles.dateLabel}>{today}</span>
          <p style={styles.dateDesc}>Solve today's puzzle and compete on the leaderboard!</p>

          {attemptLoading ? (
            <p style={styles.attemptsText}>Loading...</p>
          ) : attemptError ? (
            <p style={styles.errorText}>Unable to load attempts. Check connection.</p>
          ) : (
            <>
              <p style={styles.attemptsText}>
                Attempts: {attemptsRemaining} / {maxAttempts}
              </p>

              <button
                style={{
                  ...styles.playBtn,
                  ...(!canPlay ? { opacity: 0.5 } : {}),
                }}
                onClick={handlePlay}
                disabled={!canPlay}
              >
                {consuming
                  ? 'Starting...'
                  : attemptsRemaining > 0
                    ? "Play Today's Puzzle"
                    : 'No attempts remaining'}
              </button>

              {attemptsRemaining === 0 && canBuyAttempt && (
                <button
                  style={styles.buyBtn}
                  onClick={handleBuyAttempt}
                  disabled={purchasing}
                >
                  {purchasing ? 'Processing...' : `Buy +1 Attempt (${ATTEMPT_PRICE_STARS} Stars)`}
                </button>
              )}

              {attemptsRemaining === 0 && !canBuyAttempt && (
                <p style={styles.noAttemptsText}>Come back tomorrow!</p>
              )}
            </>
          )}
        </div>

        <div style={styles.streakCard}>
          <span style={styles.streakNum}>{gameState.streak}</span>
          <span style={styles.streakLabel}>Day Streak</span>
        </div>

        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Today's Leaderboard</h3>
          {loading ? (
            <p style={styles.loading}>Loading...</p>
          ) : leaderboard.length === 0 ? (
            <p style={styles.emptyText}>No scores yet. Be the first!</p>
          ) : (
            <div style={styles.list}>
              {leaderboard.slice(0, 10).map((entry, i) => (
                <div key={i} style={styles.listItem}>
                  <span style={styles.rank}>#{i + 1}</span>
                  <span style={styles.name}>{entry.displayName || entry.username || 'Anonymous'}</span>
                  <span style={styles.score}>{entry.moves} moves</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, any> = {
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: '#4ecdc4',
    fontSize: '16px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    padding: '8px',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
    color: '#eee',
  },
  content: {
    flex: 1,
    padding: '16px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  dateCard: {
    background: 'rgba(78, 205, 196, 0.1)',
    border: '1px solid rgba(78, 205, 196, 0.2)',
    borderRadius: '16px',
    padding: '20px',
    textAlign: 'center',
  },
  dateLabel: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#4ecdc4',
  },
  dateDesc: {
    fontSize: '13px',
    color: '#aaa',
    marginTop: '8px',
  },
  attemptsText: {
    fontSize: '14px',
    color: '#eee',
    marginTop: '12px',
    fontWeight: '600',
  },
  errorText: {
    fontSize: '14px',
    color: '#ff6b6b',
    marginTop: '12px',
    fontWeight: '600',
  },
  playBtn: {
    marginTop: '12px',
    width: '100%',
    padding: '14px',
    background: 'linear-gradient(135deg, #4ecdc4, #44b3aa)',
    border: 'none',
    borderRadius: '12px',
    color: '#1a1a2e',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  buyBtn: {
    marginTop: '8px',
    width: '100%',
    padding: '12px',
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '12px',
    color: '#eee',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  noAttemptsText: {
    fontSize: '13px',
    color: '#888',
    marginTop: '8px',
  },
  streakCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '16px',
    background: 'rgba(255,255,255,0.05)',
    borderRadius: '12px',
  },
  streakNum: {
    fontSize: '36px',
    fontWeight: '800',
    color: '#feca57',
  },
  streakLabel: {
    fontSize: '12px',
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: '2px',
  },
  section: {
    marginTop: '8px',
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#eee',
    margin: '0 0 12px',
  },
  loading: {
    color: '#888',
    textAlign: 'center',
    fontSize: '14px',
  },
  emptyText: {
    color: '#666',
    textAlign: 'center',
    fontSize: '14px',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 12px',
    background: 'rgba(255,255,255,0.04)',
    borderRadius: '10px',
  },
  rank: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#4ecdc4',
    width: '32px',
  },
  name: {
    flex: 1,
    fontSize: '14px',
    color: '#eee',
  },
  score: {
    fontSize: '13px',
    color: '#aaa',
  },
};
